import { Tooltip, Tag } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import { SeverityLabel } from './styled';
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import React from 'react';
import RootCauseTitle from './RootCauseTitle';

export const SEVERITY_CONFIG = {
  Critical: {
    key: 'Critical',
    color: '#D0021B',
    bgColor: 'red',
    label: <SeverityLabel color="#D0021B">Critical</SeverityLabel>,
    icon: <WarningOutlined style={{ color: '#D0021B', fontSize: '14px' }} />,
    order: 3,
  },
  Warning: {
    key: 'Warning',
    color: '#FF9300',
    bgColor: 'gold',
    label: <SeverityLabel color="#FF9300">Warning</SeverityLabel>,
    icon: <ExclamationCircleOutlined style={{ color: '#FF9300' }} />,
    order: 2,
  },
  Info: {
    key: 'Info',
    color: '#788BA9',
    bgColor: 'geekblue',
    label: <SeverityLabel color="#788BA9">Info</SeverityLabel>,
    icon: <InfoCircleOutlined style={{ color: '#788BA9' }} />,
    order: 1,
  },
  Ended: {
    key: 'Ended',
    color: '#4A4A4A',
    label: <SeverityLabel color="#4A4A4A">Ended</SeverityLabel>,
    icon: <ClockCircleOutlined style={{ color: '4A4A4A', marginRight: '3px' }} />,
    order: 0,
  },
};

export const getSecsFromDuration = (duration = '') => {
  duration = (duration || '').trim();
  let res = 0;
  if (duration?.split(' ')?.length !== 3) {
    return res;
  }
  const [h, m, s] = duration.split(' ');
  res += +h?.split('h')?.[0] * 3600;
  res += +m?.split('m')?.[0] * 60;
  res += +s?.split('s')?.[0];
  return res;
};

export const getColumns = () => [
  {
    title: 'Peak Severity',
    dataIndex: 'peakSeverity',
    key: 'peakSeverity',
    width: 120,
    render: (severity, record) => {
      return (
        <span>
          <Tag
            color={SEVERITY_CONFIG[severity].bgColor}
            style={{ marginLeft: !record || !record.alertEvents ? '13px' : 0 }}
          >
            <span style={{ marginRight: '3px' }}>{SEVERITY_CONFIG[severity].icon}</span>
            {SEVERITY_CONFIG[severity].label}
          </Tag>
        </span>
      );
    },
    sorter: (a, b) => SEVERITY_CONFIG[a.peakSeverity].order - SEVERITY_CONFIG[b.peakSeverity].order,
  },
  {
    title: 'Metric',
    dataIndex: 'metricDisplayName',
    key: 'metricDisplayName',
    width: 250,
    render: (metricDisplayName, record) =>
      record.isDeletedMetric ? (
        <Tooltip title="This metric has been deleted.">{metricDisplayName}</Tooltip>
      ) : (
        <div> {metricDisplayName} </div>
      ),
    sorter: (a, b) =>
      (a.metricDisplayName || '')
        .toLowerCase()
        .localeCompare((b.metricDisplayName || '').toLowerCase()),
  },
  {
    title: 'Impacted Cohorts',
    dataIndex: 'rootCauseDisplay',
    key: 'rootCauseDisplay',
    width: 330,
    render: (_, record) => {
      if (!record || !record.alertEvents) {
        return null;
      }
      return <RootCauseTitle record={record} />;
    },
    sorter: (a, b) =>
      (a.rootCauseDisplay || '')
        .toLowerCase()
        .localeCompare((b.rootCauseDisplay || '').toLowerCase()),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 90,
    sorter: (a, b) => a.hasEnded - b.hasEnded,
  },
  {
    title: 'Peak Value',
    dataIndex: 'peakValueDisplay',
    key: 'peakValueDisplay',
    width: 110,
    sorter: (a, b) => a.peakValue - b.peakValue,
  },
  {
    title: 'Time Alert Fired',
    dataIndex: 'eventTime',
    key: 'eventTime',
    width: 160,
    sorter: (a, b) => (moment(a.eventTimestamp).isBefore(moment(b.eventTimestamp)) ? -1 : 1),
    defaultSortOrder: 'descend' as SortOrder,
  },
  {
    title: 'Duration',
    dataIndex: 'duration',
    key: 'duration',
    width: 110,
    sorter: (a, b) => {
      const firstItem = getSecsFromDuration(a.duration);
      const secondItem = getSecsFromDuration(b.duration);
      if (firstItem > secondItem) {
        return 1;
      } else if (firstItem === secondItem) {
        return 0;
      }
      return -1;
    },
  },
];
